import Vue from 'vue'
import VueRouter from 'vue-router'
import router from '@/router'
import store from '@/store'
import { analyticsService } from '@/services/analyticsService'

/* navigational routes */

const errorRoute = (error) => ({
  name: 'error',
  replace: true,
  query: {
    code: error.name || 'SystemError',
    message: error.message
  }
})

const loginRoute = (to) => ({
  name: 'login',
  replace: true,
  query: {
    options: to.query?.options, // forward any login options
    redirect: to.fullPath // includes path, query and hash
  }
})

const redirectRoute = (redirectURI) => {
  const url = new URL(redirectURI, window.location.origin)
  const queryParams = Object.fromEntries(url.searchParams)
  const { route } = router.resolve({ path: redirectURI })
  return {
    ...route,
    replace: true,
    // path: url.pathname,
    query: queryParams
  }
}

const susiRoute = (to) => ({
  name: 'susi',
  replace: false,
  query: {
    options: to.query?.options, // forward any login options
    redirect: to.fullPath // includes path, query and hash
  }
})

/* route guards */

// ensure authentication / authorization
const authGuard = async (to, from) => {
  const authService = router.app.$auth
  const realmType = store.state.tenantStore.tenant.realmType
  console.debug('[router:authGuard]: realmType=', realmType)

  let nextRoute = null
  let redirectURI = null

  const requiresAuthentication = (route) => {
    // const requiresAuth = route.matched.some((r) => r.meta.requiresAuthentication)

    if (!realmType || realmType === 'private') {
      return route.meta?.requiresAuthentication
    }

    if (realmType === 'protected') {
      return route.meta?.requiresAuthentication && route.meta?.tier > 1
    }

    if (realmType === 'public') {
      return false
    }

    return route.meta?.requiresAuthentication
  }

  try {
    // wait for store to be loaded
    // await router.app.$store.restored

    // wait for authentication service
    // await authService.waitUntilReady()

    // starting a new session
    if (!authService.isAuthenticated() && from === VueRouter.START_LOCATION) {
      // is redirect after login?
      const redirectResponse = await authService.handleRedirect()
      redirectURI = redirectResponse?.state?.redirectURI || null
      if (redirectURI) {
        nextRoute = redirectRoute(redirectURI)
      }
      console.debug('[router:authGuard]: redirectURI=', redirectURI)

      // user may have logged in via a previous session
      if (!authService.isAuthenticated()) {
        await authService.restoreSession()
      }

      // FIXME: set locale in i18nStore (if state.locale is set and different than current locale)
      // locale = state?.locale
    }

    // is route protected?
    if (requiresAuthentication(nextRoute || to)) {
      if (authService.isAuthenticated()) {
        // authorize the (already authenticated) user
        if (!authService.isAuthorized()) {
          console.debug('[router:authGuard]: Authorizing....')
          const userId = authService.getUserId()
          await authService.authorize(userId) // throws error if not authorized
        }

        // at this point, user is both authenticated *and* authorized
        console.debug(
          `[router:authGuard]: ${redirectURI ? 'Redirecting....' : 'Proceeding to route...'}`
        )
      } else {
        console.debug('[router:authGuard]: Requires authentication....')
        nextRoute = realmType === 'protected' ? susiRoute(to) : loginRoute(to)
      }
    }

    // retrieve authentication & authorization status
    console.debug('[router:authGuard]: authenticated=', authService.isAuthenticated())
    console.debug('[router:authGuard]: authorized=', authService.isAuthorized())
  } catch (error) {
    console.error('[router:authGuard]: An error occured.', error)
    // logout unauthorized users so they can login with a different userId
    if (authService.isAuthenticated()) {
      authService.signOut()
    }
    nextRoute = errorRoute(error)
  }

  return nextRoute
}

// restrict routes to those configured for this tenant
const configGuard = (_to, _from, _next) => {
  // return router.app.$store.state.menuStore.menuRoutes.includes(to.name)
  return true
}

// record route changes for analytics
const pageViewGuard = (to) => {
  const capture = to.matched.some((route) => route.meta.capture !== false)
  if (capture) {
    analyticsService.capture('$pageView', { page_type: 'route', page_name: to.name })
  }
}
const pageLeaveGuard = (from) => {
  const capture = from?.matched.some((route) => route.meta.capture !== false)

  if (capture) {
    analyticsService.capture('$pageleave', { page_type: 'route', page_name: from?.name })
  }
}

// saves the scroll position of the "from" page
const scrollGuard = (to, from) => {
  const position = {
    x: window.scrollX,
    y: window.scrollY
  }
  console.debug(`[router:scrollGuard]: scroll position=(${position.x}, ${position.y})`)
  from.meta.scrollPosition = position
}

/* router guards */

// called before navigating to the route
export const beforeEach = async (to, from, next) => {
  console.warn(`[router:beforeEach]: Routing to ${to.fullPath}...`)
  const nextRoute = await authGuard(to, from)
  pageLeaveGuard(from)
  nextRoute ? await next(nextRoute) : await next()
}

// called before component loads, but after in-component guards run
export const beforeResolve = (to, from, next) => {
  const isActive = configGuard(to, from, next)
  isActive ? next() : next({ name: 'error404' })
}

// called just before the "to" component DOM renders
export const afterEach = (to, from) => {
  pageViewGuard(to)
  Vue.nextTick(() => {
    if (to.name === 'iplayer') {
      scrollGuard(to, from)
    }
  })
}
